import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { alpha } from "@mui/material/styles";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSIconButton from "views/system-design/DSIconButton";
import DSCard from "views/system-design/DSCard";
import { Ticket } from "../../types";
import TicketChip from "./TicketChip";
import {
  DAYS_OF_WEEK,
  getStartOfWeek,
  isSameDay,
  formatWeekRange,
} from "./utils";

interface WeekViewProps {
  currentDate: Date;
  onDateChange: (date: Date) => void;
  ticketsByDay: Map<string, Ticket[]>;
  onSelectTicket: (ticket: Ticket) => void;
}

const DAYS_IN_WEEK = 7;

export default function WeekView({
  currentDate,
  onDateChange,
  ticketsByDay,
  onSelectTicket,
}: WeekViewProps) {
  const weekStart = getStartOfWeek(currentDate);
  const today = new Date();
  const weekDays: Date[] = [];
  for (let dayIndex = 0; dayIndex < DAYS_IN_WEEK; dayIndex++) {
    const dayDate = new Date(weekStart);
    dayDate.setDate(weekStart.getDate() + dayIndex);
    weekDays.push(dayDate);
  }

  const handlePrevWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() - DAYS_IN_WEEK);
    onDateChange(newDate);
  };

  const handleNextWeek = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + DAYS_IN_WEEK);
    onDateChange(newDate);
  };

  return (
    <DSBox sx={{ p: 2 }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <DSIconButton onClick={handlePrevWeek}>
          <FaChevronLeft />
        </DSIconButton>
        <DSTypography variant="h5">{formatWeekRange(weekStart)}</DSTypography>
        <DSIconButton onClick={handleNextWeek}>
          <FaChevronRight />
        </DSIconButton>
      </DSBox>

      <DSBox
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${DAYS_IN_WEEK}, 1fr)`,
          gap: 1,
        }}
      >
        {weekDays.map((dayDate, dayIndex) => {
          const isToday = isSameDay(dayDate, today);
          const dayTickets = ticketsByDay.get(dayDate.toDateString()) ?? [];
          return (
            <DSCard
              key={dayDate.toISOString()}
              sx={{
                minHeight: 320,
                p: 1,
                border: isToday ? 2 : 1,
                borderColor: isToday ? "primary.main" : "divider",
                backgroundColor: isToday
                  ? (theme) => alpha(theme.palette.primary.main, 0.12)
                  : "background.paper",
                boxShadow: isToday
                  ? (theme) =>
                      `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`
                  : undefined,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DSBox sx={{ textAlign: "center", mb: 1 }}>
                <DSTypography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  {DAYS_OF_WEEK[dayIndex]}
                </DSTypography>
                <DSTypography
                  variant="body1"
                  sx={{
                    fontWeight: isToday ? "bold" : 500,
                    color: isToday ? "primary.main" : "text.primary",
                  }}
                >
                  {dayDate.getDate()}
                </DSTypography>
              </DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  overflow: "auto",
                  flex: 1,
                }}
              >
                {dayTickets.map((ticket) => (
                  <TicketChip
                    key={ticket.id}
                    ticket={ticket}
                    onSelectTicket={onSelectTicket}
                  />
                ))}
              </DSBox>
            </DSCard>
          );
        })}
      </DSBox>
    </DSBox>
  );
}
